/** Same `code` `enforceOrganizationSite` puts on its 403 body. */
export const ORGANIZATION_SITE_CODE = 'organization_site';

/** Auth-layout route that explains which site the account has to use. */
export const WRONG_SITE_PATH = '/auth/error/wrong_site';

/**
 * Set before sign-out so the logout redirect does not send the browser to the login page
 * and reload it over `/auth/error/wrong_site`.
 */
export const WRONG_SITE_PENDING_KEY = 'spaq.wrongSite';

const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const SPAQ_PARENTS = ['spaq.com', 'spaq.app', 'spaq.dev', 'spaq.web'];

/**
 * Builds the organization site URL for the wrong-site page.
 * A full hostname (`saaq.spaq.com`, from `/organizations/{code}/domain`) is used as-is.
 * A bare label (`saaq`) on `cem.spaq.com` becomes `https://saaq.spaq.com`.
 * Returns null when the result is not a SPAQ hostname.
 */
export function organizationSiteUrl(domainPrefix: string | null | undefined, currentHostname: string): string | null {
	if (!domainPrefix) return null;
	const prefix = domainPrefix.trim().toLowerCase().replace(/\.$/, '');
	if (!prefix || prefix.includes('..')) return null;

	let host: string;
	if (prefix.includes('.')) {
		host = prefix;
	} else {
		if (!LABEL.test(prefix)) return null;
		const current = currentHostname.trim().toLowerCase().replace(/:\d+$/, '').replace(/\.$/, '');
		const dot = current.indexOf('.');
		const parent = dot === -1 ? 'spaq.com' : current.slice(dot + 1);
		host = `${prefix}.${parent}`;
	}

	return spaqSiteUrl(host);
}

/**
 * Accepts only an https URL on a SPAQ host, with no credentials, path, query, or hash.
 * The `site` query param is user-controllable, so the landing page must not render it raw.
 */
export function readOrganizationSiteLink(raw: string | null | undefined): string | null {
	if (!raw) return null;
	let url: URL;
	try {
		url = new URL(raw);
	} catch {
		return null;
	}
	if (url.protocol !== 'https:') return null;
	if (url.username || url.password) return null;
	if (url.pathname !== '/' && url.pathname !== '') return null;
	if (url.search || url.hash) return null;
	return spaqSiteUrl(url.hostname);
}

function spaqSiteUrl(hostname: string): string | null {
	const host = hostname.trim().toLowerCase().replace(/\.$/, '');
	const onSpaq = SPAQ_PARENTS.some(parent => host === parent || host.endsWith(`.${parent}`));
	if (!onSpaq) return null;
	if (!host.split('.').every(part => LABEL.test(part))) return null;
	return `https://${host}`;
}
